interface AuthCredentials {
  email: string
  password: string
}

interface SignupCredentials {
  email: string
  password: string
  first_name: string
  last_name: string
  password_confirmation: string
}

interface ApiResponse<T = any> {
  success: boolean
  message?: string
  error?: string
  data?: T
  errors?: string[]
}

interface User {
  id?: number
  email: string
  first_name: string
  last_name: string
  full_name: string
  role: string
  provider?: string
  image_url?: string
  is_active: boolean
  needs_onboarding?: boolean
}

interface AuthResponse {
  user: User
  token: string
  expires_in: number
}

interface ProfileResponse {
  success: boolean
  user: User
  companies: any[]
  has_company: boolean
  needs_onboarding: boolean
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1'

class ApiService {
  private getAuthHeaders(): HeadersInit {
    const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null
    const companyId = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...(companyId && { 'X-Company-ID': companyId }),
    }
  }

  async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${API_BASE_URL}${endpoint}`
    
    const config: RequestInit = {
      headers: {
        ...this.getAuthHeaders(),
        ...options.headers,
      },
      ...options,
    }

    try {
      console.log('API Request:', { url, method: config.method || 'GET', headers: config.headers })
      
      const response = await fetch(url, config)
      const data = await response.json()
      
      console.log('API Response:', { status: response.status, data })
      
      // For non-2xx responses, still return the data so the component can handle success/failure
      if (!response.ok) {
        console.error('API Error:', { status: response.status, data })
        // If the API returns a structured error response, return it
        if (data && typeof data === 'object') {
          return data
        }
        // Otherwise throw a generic error
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      return data
    } catch (error) {
      console.error('API Network Error:', error)
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Network error occurred')
    }
  }

  async login(credentials: AuthCredentials): Promise<any> {
    // The actual API returns { success: true, user: User, token: string }
    // Not wrapped in a 'data' field like ApiResponse expects
    return this.request<any>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    })
  }

  async register(credentials: SignupCredentials): Promise<any> {
    // The actual API returns { success: true, message: string, user: User, token: string }
    // Not wrapped in a 'data' field like ApiResponse expects
    return this.request<any>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(credentials),
    })
  }

  async getProfile(): Promise<ProfileResponse> {
    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      headers: this.getAuthHeaders(),
    })
    return response.json()
  }

  async initGoogleOAuth(): Promise<void> {
    const redirectUrl = `${window.location.origin}/auth/callback`
    window.location.href = `${API_BASE_URL}/oauth/google?redirect_url=${encodeURIComponent(redirectUrl)}`
  }

  async handleOAuthCallback(code: string, state?: string): Promise<ApiResponse<AuthResponse>> {
    return this.request<AuthResponse>('/oauth/callback', {
      method: 'POST',
      body: JSON.stringify({ code, state, provider: 'google' }),
    })
  }

  async getDashboardSummary() {
    return this.request('/dashboard/summary')
  }

  async getCompanies() {
    return this.request('/companies')
  }

  async completeOnboarding(payload: {
    company_name: string
    onboarding_answers: {
      answers: Array<{
        question_id: string
        question: string
        field: string
        type: string
        answer: string | string[]
        answer_label?: string
        answer_labels?: string[]
        answered_at: string
      }>
      metadata: {
        completed: boolean
        completed_at: string
        started_at: string
        version: string
        total_questions: number
        answered_questions: number
      }
    }
  }) {
    return this.request('/companies/complete_onboarding', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  async getChartOfAccounts() {
    return this.request('/chart_of_accounts')
  }

  async clearChartOfAccounts() {
    return this.request('/chart_of_accounts/clear_all', {
      method: 'DELETE',
    })
  }

  async getAccountTypes() {
    return this.request('/chart_of_accounts/types')
  }

  async getAccountBalance(id: string, asOfDate?: string) {
    const params = asOfDate ? `?as_of_date=${asOfDate}` : ''
    return this.request(`/chart_of_accounts/${id}/balance${params}`)
  }

  async createChartOfAccount(accountData: {
    account_code: string
    account_name: string
    account_type: string
    account_subtype?: string
    description?: string
    parent_account_id?: string
    is_active?: boolean
  }) {
    return this.request('/chart_of_accounts', {
      method: 'POST',
      body: JSON.stringify({ account: accountData }),
    })
  }

  async updateChartOfAccount(id: string, accountData: {
    account_code?: string
    account_name?: string
    account_type?: string
    account_subtype?: string
    description?: string
    parent_account_id?: string
    is_active?: boolean
  }) {
    return this.request(`/chart_of_accounts/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ account: accountData }),
    })
  }

  async deleteChartOfAccount(id: string) {
    return this.request(`/chart_of_accounts/${id}`, {
      method: 'DELETE',
    })
  }

  async getInvoices() {
    return this.request('/invoices')
  }

  async getCustomers() {
    return this.request('/customers')
  }

  async getVendors() {
    return this.request('/vendors')
  }

  async getBankAccounts() {
    return this.request('/bank_accounts')
  }

  async getReports(type: string, params?: Record<string, any>) {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : ''
    return this.request(`/reports/${type}${queryString}`)
  }

  async getBalanceSheet(asOfDate?: string) {
    const params = asOfDate ? `?as_of_date=${asOfDate}` : ''
    return this.request(`/reports/balance_sheet${params}`)
  }

  async getProfitAndLoss(startDate?: string, endDate?: string) {
    const params = new URLSearchParams()
    if (startDate) params.append('start_date', startDate)
    if (endDate) params.append('end_date', endDate)
    const queryString = params.toString() ? `?${params.toString()}` : ''
    return this.request(`/reports/profit_and_loss${queryString}`)
  }

  async downloadFile(url: string, filename: string) {
    const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null
    const companyId = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null
    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1'
    const fileUrl = url.startsWith('http') ? url : `${baseUrl.replace('/api/v1', '')}${url}`

    return fetch(fileUrl, {
      method: 'GET',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...(companyId && { 'X-Company-ID': companyId }),
      },
    }).then(response => {
      if (!response.ok) {
        throw new Error(`Failed to download file: ${response.statusText}`)
      }
      return response.blob()
    }).then(blob => {
      // Create a blob URL and trigger download
      const blobUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)
    })
  }

  async getSuggestedBill(fileUploadId: string) {
    return this.request(`/file_uploads/${fileUploadId}/suggested_bill`)
  }

  async createBillFromUpload(fileUploadId: string, autoPost: boolean = true) {
    console.log('API: Creating bill from upload', { fileUploadId, autoPost })

    const companyId = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null
    console.log('API: Company ID from localStorage:', companyId)

    const requestBody = {
      auto_post_transaction: autoPost
    }

    console.log('API: Request body:', requestBody)

    return this.request(`/file_uploads/${fileUploadId}/create_bill`, {
      method: 'POST',
      body: JSON.stringify(requestBody)
    })
  }

  async postBillTransaction(billId: string) {
    return this.request(`/bills/${billId}/post_transaction`, {
      method: 'POST'
    })
  }

  async updateLineItemAccount(fileUploadId: string, data: {
    line_item_index: number
    account_code: string
    change_reason: string
  }) {
    return this.request(`/file_uploads/${fileUploadId}/update_line_item_account`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  async batchUpdateLineItemAccounts(fileUploadId: string, data: {
    line_items: Array<{
      line_item_index: number
      account_code: string
      change_reason: string
    }>
  }) {
    return this.request(`/file_uploads/${fileUploadId}/batch_update_line_item_accounts`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  async getFileUploads(params?: {
    page?: number
    per_page?: number
  }) {
    const queryString = params ? `?${new URLSearchParams(params as any).toString()}` : ''
    return this.request(`/file_uploads${queryString}`)
  }

  async getFileUpload(id: string) {
    return this.request(`/file_uploads/${id}`)
  }

  async getBill(billId: string) {
    return this.request(`/bills/${billId}`)
  }

  async getTransactions(params?: {
    page?: number
    per_page?: number
    status?: 'draft' | 'posted' | 'void'
    transaction_type?: string
    start_date?: string
    end_date?: string
  }) {
    const queryString = params ? `?${new URLSearchParams(params as any).toString()}` : ''
    return this.request(`/transactions${queryString}`)
  }

  async getTransaction(transactionId: string) {
    return this.request(`/transactions/${transactionId}`)
  }

  async getJournalView(params?: {
    page?: number | string
    per_page?: number | string
    status?: string
    transaction_type?: string
    start_date?: string
    end_date?: string
  }): Promise<ApiResponse<any>> {
    const queryString = params ? `?${new URLSearchParams(params as any).toString()}` : ''
    return this.request<any>(`/transactions/journal${queryString}`)
  }

  async createChatSession(data: {
    instruction?: string
    files?: File[]
    title?: string
  }) {
    const formData = new FormData()
    
    if (data.instruction) {
      formData.append('instruction', data.instruction)
    }
    
    if (data.title) {
      formData.append('title', data.title)
    }
    
    if (data.files && data.files.length > 0) {
      data.files.forEach(file => {
        formData.append('files[]', file)
      })
    }

    const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null
    const companyId = typeof window !== 'undefined' ? localStorage.getItem('companyId') : null

    return fetch(`${API_BASE_URL}/chat_sessions`, {
      method: 'POST',
      headers: {
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...(companyId && { 'X-Company-ID': companyId }),
      },
      body: formData
    }).then(async response => {
      const data = await response.json()
      if (!response.ok) {
        if (data && typeof data === 'object') {
          return data
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      return data
    }).catch(error => {
      if (error instanceof Error) {
        throw error
      }
      throw new Error('Network error occurred')
    })
  }
}

export const apiService = new ApiService()
export type { AuthCredentials, SignupCredentials, ApiResponse, User, AuthResponse, ProfileResponse }
